import { useMutation, useQuery } from "@apollo/client";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import CompareArrowsOutlined from "@mui/icons-material/CompareArrowsOutlined";
import HighlightOff from "@mui/icons-material/HighlightOff";
import Refresh from "@mui/icons-material/Refresh";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Title, useCanAccess, useTranslate } from "react-admin";
import { Link as RouterLink } from "react-router-dom";

import { CrmCard } from "../components/crm/CrmCard";
import {
  CrmDuplicateComparison,
  CrmDuplicateReasonChips,
  CrmDuplicateScore,
  CrmDuplicateStatusChip,
  scoreBand,
} from "../components/crm/CrmDuplicateReview";
import { CrmObjectIcon } from "../components/crm/CrmObjectIcon";
import { CrmPageHeader } from "../components/crm/CrmPageHeader";
import { ListPagination } from "../components/ListPagination";
import { RequirePermission } from "../components/RequirePermission";
import { ROWS_PER_PAGE } from "../constants";
import { ResourceName } from "../constants/resources";
import {
  CrmDuplicateCandidateFieldsFragment,
  CrmDuplicateReviewStatus,
  CrmDuplicateScanState,
  CrmDuplicateStatusFilter,
  CrmObject,
  CustomerServiceCrmDuplicatesDocument,
  CustomerServiceCrmDuplicatesQuery,
  CustomerServiceCrmDuplicatesQueryVariables,
  ReviewCustomerServiceCrmDuplicateDocument,
  ReviewCustomerServiceCrmDuplicateMutation,
  ReviewCustomerServiceCrmDuplicateMutationVariables,
} from "../generated/graphql";
import { CRM_COLORS, crmRecordPath, formatCrmDateTime } from "../lib/crm";
import { dialogActionsSx, dialogContentSx, dialogPaperSx, dialogTitleSx } from "../theme/dialogStyles";

const MIN_SCORE = 50;
type Candidate = CrmDuplicateCandidateFieldsFragment;

const headCellSx = {
  backgroundColor: CRM_COLORS.tableHeader,
  color: CRM_COLORS.label,
  fontSize: 12,
  fontWeight: 700,
};

const CrmDuplicatesContent = () => {
  const translate = useTranslate();
  const label = (key: string) => translate(`crm.duplicates.${key}`);
  const [object, setObject] = useState<CrmObject | "">("");
  const [minScore, setMinScore] = useState(MIN_SCORE);
  const [status, setStatus] = useState(CrmDuplicateStatusFilter.Pending);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE);
  const [selected, setSelected] = useState<Candidate | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [refreshWarning, setRefreshWarning] = useState(false);
  const savingRef = useRef(false);
  const { canAccess: canReview, isPending: permissionPending } = useCanAccess({
    action: "list",
    resource: ResourceName.CrmDuplicateReview,
  });
  const { data, loading, error, refetch } = useQuery<
    CustomerServiceCrmDuplicatesQuery,
    CustomerServiceCrmDuplicatesQueryVariables
  >(CustomerServiceCrmDuplicatesDocument, {
    variables: { input: { object: object || null, minScore, status, offset: page * rowsPerPage, limit: rowsPerPage } },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const [review] = useMutation<
    ReviewCustomerServiceCrmDuplicateMutation,
    ReviewCustomerServiceCrmDuplicateMutationVariables
  >(ReviewCustomerServiceCrmDuplicateDocument);
  const list = data?.customerServiceCrmDuplicates;
  useEffect(() => {
    setSelected(null);
  }, [object, minScore, status, page, rowsPerPage, list, loading, error]);
  useEffect(() => {
    if (!list || loading || error) return;
    const lastPage = Math.max(0, Math.ceil(list.totalCount / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [list, loading, error, page, rowsPerPage]);
  const changeFilter = () => {
    setPage(0);
    setSelected(null);
    setSaveError(false);
    setRefreshWarning(false);
  };
  const refresh = async () => {
    setSelected(null);
    try {
      await refetch();
      setRefreshWarning(false);
    } catch {
      /* The query error is rendered below. */
    }
  };
  const save = async (desiredStatus: CrmDuplicateReviewStatus) => {
    if (!selected || canReview !== true || permissionPending || savingRef.current || loading || error) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(false);
    try {
      try {
        await review({
          variables: { input: { id: selected.id, expectedFingerprint: selected.fingerprint, status: desiredStatus } },
        });
      } catch {
        setSaveError(true);
        await refresh();
        return;
      }
      setSelected(null);
      try {
        await refetch();
        setRefreshWarning(false);
      } catch {
        setRefreshWarning(true);
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  return (
    <Box sx={{ color: CRM_COLORS.text }}>
      <Title title={label("title")} />
      <CrmPageHeader
        icon={<CompareArrowsOutlined />}
        overline={translate("crm.title")}
        title={label("title")}
        meta={
          list?.scannedAt
            ? translate("crm.duplicates.scannedAt", { time: formatCrmDateTime(list.scannedAt) })
            : undefined
        }
        actions={
          <Button
            disabled={saving || loading}
            onClick={refresh}
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            sx={{ textTransform: "none", backgroundColor: CRM_COLORS.cardBackground }}
          >
            {label("refresh")}
          </Button>
        }
      >
        <Typography variant="body2" sx={{ px: 2, pb: 2 }}>
          {label("explanation")}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, px: 2, pb: 2 }}>
          <TextField
            select
            size="small"
            sx={{ minWidth: 200, width: { xs: "100%", sm: "auto" } }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            label={label("object")}
            value={object}
            onChange={event => {
              setObject(event.target.value as CrmObject | "");
              changeFilter();
            }}
            disabled={saving}
          >
            <MenuItem value="">{label("allObjects")}</MenuItem>
            {[CrmObject.Account, CrmObject.Lead].map(value => (
              <MenuItem key={value} value={value}>
                {translate(`crm.objects.${value}.many`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            sx={{ minWidth: 200, width: { xs: "100%", sm: "auto" } }}
            label={label("minScore")}
            value={minScore}
            onChange={event => {
              setMinScore(Number(event.target.value));
              changeFilter();
            }}
            disabled={saving}
          >
            {Array.from({ length: 51 }, (_, index) => MIN_SCORE + index).map(score => (
              <MenuItem key={score} value={score}>
                {score}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            sx={{ minWidth: 200, width: { xs: "100%", sm: "auto" } }}
            label={label("status")}
            value={status}
            onChange={event => {
              setStatus(event.target.value as CrmDuplicateStatusFilter);
              changeFilter();
            }}
            disabled={saving}
          >
            {Object.values(CrmDuplicateStatusFilter).map(value => (
              <MenuItem key={value} value={value}>
                {label(`statuses.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </CrmPageHeader>
      {saveError && <Alert severity="error">{label("saveError")}</Alert>}
      {refreshWarning && <Alert severity="warning">{label("reviewRefreshWarning")}</Alert>}
      {loading ? (
        <CircularProgress aria-label={translate("crm.loading")} />
      ) : error ? (
        <Alert severity="error">{label("loadError")}</Alert>
      ) : !list ? null : list.scanState === CrmDuplicateScanState.NotScanned ? (
        <Alert severity="info">{label("notScanned")}</Alert>
      ) : list.scanState === CrmDuplicateScanState.Suppressed ? (
        <Alert severity="info">{label("suppressed")}</Alert>
      ) : (
        <CrmCard flush>
          {!list.candidates.length ? (
            <Typography sx={{ p: 2 }}>{label("empty")}</Typography>
          ) : (
            <TableContainer>
              <Table aria-label={label("title")} size="small">
                <TableHead>
                  <TableRow>
                    {["score", "records", "reasonsTitle", "status", "compare"].map(key => (
                      <TableCell key={key} sx={headCellSx}>
                        {label(key)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.candidates.map(candidate => (
                    <TableRow key={candidate.id} hover sx={{ "& > .MuiTableCell-root": { verticalAlign: "middle" } }}>
                      <TableCell sx={{ boxShadow: `inset 4px 0 0 ${scoreBand(candidate.score).color}` }}>
                        <CrmDuplicateScore score={candidate.score} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {[candidate.left, candidate.right].map(record => (
                            <Box key={`${record.object}:${record.salesforceId}`} sx={{ display: "flex", gap: 1 }}>
                              <CrmObjectIcon object={record.object} size="small" />
                              <Box sx={{ minWidth: 0 }}>
                                <Link
                                  component={RouterLink}
                                  to={crmRecordPath(record.object, record.salesforceId)}
                                  sx={{ fontWeight: 600 }}
                                >
                                  {record.name}
                                </Link>
                                <Typography variant="caption" component="div" sx={{ color: CRM_COLORS.label }}>
                                  {translate(`crm.objects.${record.object}.one`)}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <CrmDuplicateReasonChips reasons={candidate.reasons} />
                      </TableCell>
                      <TableCell>
                        <CrmDuplicateStatusChip status={candidate.status} />
                      </TableCell>
                      <TableCell>
                        <Button
                          disabled={saving}
                          onClick={() => setSelected(candidate)}
                          variant="outlined"
                          size="small"
                          startIcon={<CompareArrowsOutlined />}
                        >
                          {label("compare")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <ListPagination
            total={list.totalCount}
            page={page + 1}
            perPage={rowsPerPage}
            resource={ResourceName.Crm}
            rowsPerPageOptions={[5, 10, 25, 50]}
            onPerPageChange={next => {
              setRowsPerPage(next);
              setPage(0);
              setSelected(null);
            }}
            disabled={saving}
            isLoading={loading}
            onPageChange={nextPage => {
              setPage(nextPage - 1);
              setSelected(null);
            }}
          />
        </CrmCard>
      )}
      <Dialog
        open={!!selected && !loading && !error}
        onClose={() => {
          if (!saving) setSelected(null);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaperSx }}
        aria-labelledby="crm-duplicate-comparison-title"
      >
        <DialogTitle id="crm-duplicate-comparison-title" sx={dialogTitleSx}>
          {label("comparison")}
        </DialogTitle>
        <DialogContent sx={dialogContentSx}>
          {selected && <CrmDuplicateComparison candidate={selected} />}
        </DialogContent>
        <DialogActions sx={{ ...dialogActionsSx, flexWrap: "wrap" }}>
          {saving && <Typography role="status">{label("saving")}</Typography>}
          {canReview === true && !permissionPending && (
            <>
              <Button
                disabled={saving}
                onClick={() => save(CrmDuplicateReviewStatus.Confirmed)}
                variant="contained"
                color="primary"
                startIcon={<CheckCircleOutline />}
              >
                {label("confirm")}
              </Button>
              <Button
                disabled={saving}
                onClick={() => save(CrmDuplicateReviewStatus.Dismissed)}
                variant="outlined"
                startIcon={<HighlightOff />}
              >
                {label("dismiss")}
              </Button>
              <Button
                disabled={saving}
                onClick={() => save(CrmDuplicateReviewStatus.Deferred)}
                variant="outlined"
                startIcon={<ScheduleOutlined />}
              >
                {label("defer")}
              </Button>
            </>
          )}
          <Button disabled={saving} onClick={() => setSelected(null)} color="secondary">
            {label("close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const CrmDuplicatesPage = () => (
  <RequirePermission permission={ResourceName.Crm}>
    <CrmDuplicatesContent />
  </RequirePermission>
);
